import React from 'react';
import {
  Category as CategoryIcon,
  ExpandLess,
  ExpandMore,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  Box,
  Card,
  Chip,
  Collapse,
  Divider,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { green, grey, purple } from '@mui/material/colors';
import {
  getCategoryColor,
  getCategoryIcon,
  typography
} from '../documentHelper/DocumentHelperFunctions';

// Interfaces
interface SubcategoryData {
  subcategoryId: string;
  subcategory: string;
  templates: any[];
  count: number;
}

interface CategoryData {
  categoryId: string;
  category: string;
  subcategories: SubcategoryData[];
  totalTemplates: number;
}

interface CategorySelectorProps {
  categoryData: CategoryData[];
  selectedCategory: string;
  selectedSubcategory: string;
  expandedCategories: Set<string>;
  categoryMenuAnchor: HTMLElement | null;
  categoryMenuOpen: boolean;
  onCategoryToggle: (categoryName: string) => void;
  onSubcategorySelect: (categoryName: string, subcategoryName: string) => void;
  onCategoryMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  onCategoryMenuClose: () => void;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categoryData,
  selectedCategory,
  selectedSubcategory,
  expandedCategories,
  categoryMenuAnchor,
  categoryMenuOpen,
  onCategoryToggle,
  onSubcategorySelect,
  onCategoryMenuClick,
  onCategoryMenuClose
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Desktop Category Sidebar
  const DesktopCategorySidebar = () => (
    <Paper
      elevation={0}
      sx={{
        height: 'calc(100vh - 160px)',
        bgcolor: 'white',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CategoryIcon sx={{ color: purple[500], fontSize: 16 }} />
          <Typography
            sx={{
              ...typography.sectionTitle,
              fontWeight: 600
            }}
          >
            Categories
          </Typography>
          <Chip
            label={`${categoryData.length} Available`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: `${purple[500]}15`,
              color: purple[500]
            }}
          />
        </Stack>
      </Box>

      <Divider sx={{ mx: 1.5, opacity: 0.6 }} />

      <Box sx={{ p: 1, overflow: 'auto', height: 'calc(100% - 80px)' }}>
        <Stack spacing={0.25}>
          {categoryData.map((category) => {
            if (!category || !category.category) return null;

            const isExpanded = expandedCategories.has(category.category);
            const categoryColor = getCategoryColor(category.category);

            return (
              <Box key={category.category}>
                {/* Main Category */}
                <Card
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    boxShadow: 1,
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      boxShadow: 2,
                      backgroundColor: `${categoryColor}08`,
                      borderColor: categoryColor
                    },
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => onCategoryToggle(category.category)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: `${categoryColor}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: categoryColor
                      }}
                    >
                      {React.cloneElement(getCategoryIcon(category.category) as React.ReactElement<any>, {
                        style: { fontSize: 14 }
                      })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          ...typography.bodyText,
                          fontWeight: 600,
                          lineHeight: 1.2
                        }}
                      >
                        {category.category}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        ...typography.smallText,
                        color: categoryColor,
                        bgcolor: `${categoryColor}15`,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 0.75,
                        minWidth: 16,
                        textAlign: 'center'
                      }}
                    >
                      {category.totalTemplates}
                    </Typography>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </Card>

                {/* Subcategories */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ ml: 2, mt: 0.5 }}>
                    <Stack spacing={0.25}>
                      {category.subcategories?.map((subcategory) => {
                        if (!subcategory || !subcategory.subcategory) return null;

                        const isSelected =
                          selectedCategory === category.category &&
                          selectedSubcategory === subcategory.subcategory;

                        return (
                          <Card
                            key={subcategory.subcategoryId}
                            sx={{
                              p: 0.75,
                              borderRadius: 1,
                              boxShadow: isSelected ? 2 : 0.5,
                              cursor: 'pointer',
                              border: isSelected
                                ? `2px solid ${green[500]}`
                                : `1px solid ${theme.palette.divider}`,
                              backgroundColor: isSelected ? `${green[500]}08` : 'background.paper',
                              '&:hover': {
                                boxShadow: 1,
                                backgroundColor: `${green[500]}08`,
                                borderColor: green[500]
                              },
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() =>
                              onSubcategorySelect(category.category, subcategory.subcategory)
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box
                                sx={{
                                  width: 4,
                                  height: 16,
                                  borderRadius: 2,
                                  bgcolor: isSelected ? green[500] : grey[300]
                                }}
                              />
                              <Typography
                                sx={{
                                  ...typography.smallText,
                                  fontWeight: isSelected ? 600 : 400,
                                  flex: 1
                                }}
                              >
                                {subcategory.subcategory}
                              </Typography>
                              <Typography
                                sx={{
                                  ...typography.smallText,
                                  color: green[600],
                                  bgcolor: `${green[500]}15`,
                                  px: 0.5,
                                  py: 0.125,
                                  borderRadius: 0.5,
                                  minWidth: 16,
                                  textAlign: 'center',
                                  fontSize: '0.6rem'
                                }}
                              >
                                {subcategory.count}
                              </Typography>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );

  // Mobile Category Menu
  const MobileCategoryMenu = () => (
    <>
      <Fab
        onClick={onCategoryMenuClick}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: purple[500],
          color: 'white',
          width: 44,
          height: 44,
          '&:hover': {
            bgcolor: purple[600]
          }
        }}
      >
        <FilterIcon sx={{ fontSize: 18 }} />
      </Fab>

      <Menu
        anchorEl={categoryMenuAnchor}
        open={categoryMenuOpen}
        onClose={onCategoryMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            maxHeight: '60vh',
            width: '280px',
            borderRadius: 1.5
          }
        }}
      >
        <Box sx={{ p: 1.5, pb: 0.75 }}>
          <Typography sx={{ ...typography.bodyText, fontWeight: 600 }}>
            Select Category & Subcategory
          </Typography>
        </Box>
        <Divider sx={{ opacity: 0.6 }} />

        {categoryData.map((category) => {
          if (!category || !category.category) return null;

          const categoryColor = getCategoryColor(category.category);

          return (
            <Box key={category.category}>
              {/* Category Header */}
              <MenuItem
                sx={{
                  py: 1,
                  bgcolor: `${categoryColor}08`,
                  '&:hover': {
                    bgcolor: `${categoryColor}15`
                  }
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      bgcolor: `${categoryColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: categoryColor
                    }}
                  >
                    {React.cloneElement(getCategoryIcon(category.category) as React.ReactElement<any>, {
                      style: { fontSize: 12 }
                    })}
                  </Box>
                  <Typography
                    sx={{
                      ...typography.bodyText,
                      fontWeight: 600,
                      flex: 1
                    }}
                  >
                    {category.category}
                  </Typography>
                  <Typography
                    sx={{
                      ...typography.smallText,
                      color: categoryColor,
                      bgcolor: `${categoryColor}15`,
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.5,
                      minWidth: 16,
                      textAlign: 'center'
                    }}
                  >
                    {category.totalTemplates}
                  </Typography>
                </Stack>
              </MenuItem>

              {/* Subcategories */}
              {category.subcategories?.map((subcategory) => {
                if (!subcategory || !subcategory.subcategory) return null;

                const isSelected =
                  selectedCategory === category.category &&
                  selectedSubcategory === subcategory.subcategory;

                return (
                  <MenuItem
                    key={subcategory.subcategoryId}
                    onClick={() => onSubcategorySelect(category.category, subcategory.subcategory)}
                    sx={{
                      py: 0.75,
                      pl: 4,
                      bgcolor: isSelected ? `${green[500]}08` : 'transparent',
                      '&:hover': {
                        bgcolor: `${green[500]}08`
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          width: 4,
                          height: 16,
                          borderRadius: 2,
                          bgcolor: isSelected ? green[500] : grey[300]
                        }}
                      />
                      <Typography
                        sx={{
                          ...typography.smallText,
                          fontWeight: isSelected ? 600 : 400,
                          flex: 1
                        }}
                      >
                        {subcategory.subcategory}
                      </Typography>
                      <Typography
                        sx={{
                          ...typography.smallText,
                          color: green[600],
                          bgcolor: `${green[500]}15`,
                          px: 0.5,
                          py: 0.125,
                          borderRadius: 0.5,
                          minWidth: 16,
                          textAlign: 'center',
                          fontSize: '0.6rem'
                        }}
                      >
                        {subcategory.count}
                      </Typography>
                    </Stack>
                  </MenuItem>
                );
              })}

              {category !== categoryData[categoryData.length - 1] && (
                <Divider sx={{ opacity: 0.3 }} />
              )}
            </Box>
          );
        })}
      </Menu>
    </>
  );

  // Render based on screen size
  return (
    <>
      {!isMobile && categoryData.length > 0 && <DesktopCategorySidebar />}
      {isMobile && categoryData.length > 0 && <MobileCategoryMenu />}
    </>
  );
};

export default CategorySelector;
